import React from 'react';
import { Link } from 'react-router-dom';
import ConceptMap from '@/components/ConceptMap';
import { ArrowRight, BookOpen, Calculator, Zap } from 'lucide-react';

const Home = () => (
  <div data-testid="home-page">
    {/* Hero */}
    <div className="bg-gradient-to-br from-[#0B172A] via-slate-800 to-teal-900 text-white px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <div className="max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-teal-500/40 bg-teal-500/10 text-teal-300 text-xs font-medium mb-6">
          <Zap className="h-3 w-3" />
          Power Systems Engineering Education
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold font-heading tracking-tight mb-4 leading-tight">
          From first principles to<br className="hidden sm:block" />
          <span className="text-teal-400"> distribution design</span>
        </h1>
        <p className="text-slate-300 text-lg max-w-2xl mb-8 leading-relaxed">
          A visual learning platform for electrical engineers, EIT candidates, and distribution network designers.
          Interactive calculators, KaTeX formulas, and real utility examples.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
          to="/foundation"
          data-testid="hero-start-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-500 text-white font-medium rounded-md transition-colors duration-200"
        >
          Start with Foundation (M01)
          <ArrowRight className="h-4 w-4" />
        </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-slate-600 hover:border-slate-400 text-slate-300 hover:text-white font-medium rounded-md transition-colors duration-200"
          >
            <BookOpen className="h-4 w-4" />
            How to Use This Platform
          </Link>
        </div>
      </div>
    </div>

    {/* Stats bar */}
    <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-6">
        {[
          { label: '8 Modules', sub: 'Foundation through Distribution Design' },
          { label: '20+ Calculators', sub: 'All interactive, client-side' },
          { label: 'KaTeX Formulas', sub: 'Publication-quality math' },
          { label: '5 Live Modules', sub: 'M01 – M05 fully built' },
        ].map(({ label, sub }) => (
          <div key={label} className="flex flex-col">
            <span className="text-sm font-semibold text-slate-900 dark:text-white">{label}</span>
            <span className="text-xs text-slate-500 dark:text-slate-500">{sub}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Concept Map */}
    <div className="px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold font-heading text-slate-900 dark:text-white mb-2">
          Curriculum Map
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Hover over any node to see the module description and prerequisites. Click to navigate.
          Green border = live module. Arrows show the learning prerequisite chain.
        </p>
      </div>
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
        <ConceptMap />
      </div>
    </div>

    {/* Quick Access */}
    <div className="px-4 sm:px-6 lg:px-8 pb-16 max-w-4xl mx-auto">
      <h3 className="text-lg font-semibold font-heading text-slate-900 dark:text-white mb-4">
        Live Modules
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { to: '/foundation', testid: 'quick-access-foundation', badge: 'M01', label: 'Foundation — Three-Phase Basics', desc: 'AC phasors, three-phase power, per-phase equivalent circuits. The entry point for every module.' },
          { to: '/transformers', testid: 'quick-access-transformers', badge: 'M02', label: 'Transformer Systems', desc: 'Delta-wye connections, vector groups, leakage reactance, and nameplate impedance.' },
          { to: '/per-unit', testid: 'quick-access-per-unit', badge: 'M03', label: 'Per-Unit Analysis', desc: '9 sections: theory, KaTeX formulas, worked example, 3 calculators, and a 5-question quiz.' },
          { to: '/symmetrical-components', testid: 'quick-access-sym-comp', badge: 'M04', label: 'Symmetrical Components', desc: "Fortescue's theorem: a-operator, positive/negative/zero sequence decomposition." },
          { to: '/sequence-networks', testid: 'quick-access-seq-networks', badge: 'M05', label: 'Sequence Networks', desc: 'Build Z\u2081, Z\u2082, Z\u2080 Thevenin models. Transformer zero-sequence topology, 3Rn neutral resistor, SLG/LL/DLG network connections.' },
        ].map(({ to, testid, badge, label, desc }) => (
          <Link
            key={to}
            to={to}
            data-testid={testid}
            className="group bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-5 hover:border-teal-400 dark:hover:border-teal-600 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold font-mono">{badge}</span>
              </div>
              <div>
                <h3 className="font-semibold text-teal-900 dark:text-teal-100 mb-1 font-heading text-sm">
                  {label}
                  <span className="ml-2 text-[10px] font-bold bg-teal-600 text-white px-1.5 py-0.5 rounded-full">LIVE</span>
                </h3>
                <p className="text-xs text-teal-700 dark:text-teal-300 leading-relaxed">{desc}</p>
                <span className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-teal-700 dark:text-teal-400 group-hover:gap-2 transition-all">
                  Open module <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </div>
          </Link>
        ))}
        <Link
          to="/calculators"
          data-testid="quick-access-calculators"
          className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:border-teal-400 dark:hover:border-teal-700 transition-colors sm:col-span-2"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-800 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
              <Calculator className="h-4 w-4 text-teal-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-1 font-heading">Calculators Hub</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                All 20+ interactive calculators in one place — per-unit base, base change, transformer turns ratio, sequence decomposer, and more.
              </p>
              <span className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-slate-700 dark:text-slate-400 group-hover:gap-2 transition-all">
                Browse calculators <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </div>
        </Link>
      </div>
    </div>
  </div>
);

export default Home;
